import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'
import { QUANTUM_COLORS } from '../patron_ruptura_quantum/constants.js'
import { RESULT_LABEL, RESULT_COLOR, formatPrice, formatPct, formatTime } from './panelFormat.js'

const _LONG = 'rgb(31,123,13)' // cBuy del Pine (mismo color que la etiqueta del chart)
const _SHORT = 'rgb(186,34,46)' // cSell del Pine
const _HAIR = { borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }

function Row({ label, value, color, hint }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[11px]">
      <span className="text-[var(--ink-soft,#4A5A52)]">{label}</span>
      <span className="font-mono text-right" style={{ color: color || 'var(--ink,#1f2937)' }}>
        {value}
        {hint ? <span className="ml-1 text-[9px] text-[var(--ink-soft,#4A5A52)]">{hint}</span> : null}
      </span>
    </div>
  )
}

function Chip({ label, value, color }) {
  return (
    <span
      className="inline-flex items-baseline gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium"
      style={{ color, background: 'rgba(176, 141, 87, 0.12)' }}
    >
      {label} <span className="font-mono">{value}</span>
    </span>
  )
}

function Section({ title, children }) {
  return (
    <div className="px-2.5 pb-2" style={_HAIR}>
      <div className="pt-1.5 pb-1">
        <span className="text-[10px] font-semibold text-[var(--ink,#1f2937)]">{title}</span>
      </div>
      {children}
    </div>
  )
}

/**
 * Badge de Orderflow Quantum -- conteos descriptivos (señales/burbujas
 * visibles) + panel de stats de I3 (simulación histórica con la
 * configuración del usuario, docs/specs/Spec_Orderflow_Indicador_I3_Stats.md).
 * Layout rehecho en WP-OQ-panel-v2 sobre el molde del badge de
 * patron_ruptura_quantum (misma tarjeta paper, mismas secciones con
 * separador hair, mismo header arrastrable). Todo es display del payload:
 * cero cálculo aquí. Sin CTA de "crear bot" (I3 nunca menciona bots).
 */
export default function OrderflowQuantumBadge({
  result,
  stats,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0',
  onHeaderMouseDown,
}) {
  const data = result || {}
  const signals = Array.isArray(data.signals) ? data.signals : []
  const bubbles = Array.isArray(data.bubbles) ? data.bubbles : []
  const emitted = signals.filter(s => s.clash_state === 'emitted')
  const longs = emitted.filter(s => s.side === 'long').length
  const shorts = emitted.filter(s => s.side === 'short').length
  const precision = data.pricePrecision
  const fp = (v) => formatPrice(v, precision)

  const s = stats?.summary
  const lastSignal = stats?.last_signal
  const pct = stats?.current_levels?.pct
  const last10 = Array.isArray(stats?.last10) ? stats.last10 : []

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden w-max min-w-[270px] max-w-[min(400px,calc(100vw-1rem))]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight flex-1 truncate"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          Orderflow Quantum <span className="opacity-60 font-normal">[{version}]</span>
        </span>
        {onOpenSettings && (
          <button onClick={onOpenSettings} title="Configuración" className="p-0.5 rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Settings size={12} />
          </button>
        )}
        {onMinimize && (
          <button onClick={onMinimize} title={minimized ? 'Expandir' : 'Minimizar'} className="p-0.5 rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            {minimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
          </button>
        )}
        {onClose && (
          <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <X size={12} />
          </button>
        )}
      </div>

      {!minimized && (
        <>
          {/* Conteos: una fila horizontal de chips (defecto 1 de la spec: antes 3 filas verticales). */}
          <div className="px-2.5 pb-1.5 flex flex-wrap items-center gap-1" data-testid="oq-counts">
            <Chip label="Long ▲" value={longs} color={_LONG} />
            <Chip label="Short ▼" value={shorts} color={_SHORT} />
            <Chip label="Burbujas" value={bubbles.length} color="var(--ink,#1f2937)" />
          </div>

          {stats && (
            <>
              {/* Etiqueta permanente (spec I3 §2.5/§4.1) -- nunca tooltip, siempre visible. */}
              <p className="px-2.5 pb-1 pt-1.5 text-[9px] leading-snug italic text-[var(--ink-soft,#4A5A52)]" style={_HAIR}>
                {stats.model?.label}
              </p>

              {lastSignal && (
                <Section title="Estado">
                  <div className="flex flex-col gap-0.5" data-testid="oq-estado">
                    <Row label="Dirección" value={lastSignal.side === 'long' ? 'LONG' : 'SHORT'} color={lastSignal.side === 'long' ? _LONG : _SHORT} />
                    <Row label="Entrada" value={fp(lastSignal.entry)} />
                    <Row label="SL" value={fp(lastSignal.sl)} hint={formatPct(pct?.sl)} color={QUANTUM_COLORS.bear} />
                    <Row label="TP1" value={fp(lastSignal.tp1)} hint={formatPct(pct?.tp1)} color={QUANTUM_COLORS.bull} />
                    <Row label="TP2" value={fp(lastSignal.tp2)} hint={formatPct(pct?.tp2)} color={QUANTUM_COLORS.bull} />
                    <Row label="TP3" value={fp(lastSignal.tp3)} hint={formatPct(pct?.tp3)} color={QUANTUM_COLORS.bull} />
                    <Row label="Resultado" value={RESULT_LABEL[lastSignal.status] || lastSignal.status} color={RESULT_COLOR[lastSignal.status]} />
                  </div>
                </Section>
              )}

              {s && s.signals > 0 && (
                <Section title={`Historial (${s.signals})`}>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    <Row label="Winrate" value={`${s.winrate_pct.toFixed(0)}%`} />
                    <Row label="Abiertas" value={`${s.open_pct.toFixed(0)}%`} />
                    <Row label="TP1" value={`${s.tp1_pct.toFixed(0)}%`} color={QUANTUM_COLORS.bull} />
                    <Row label="SL" value={`${s.sl_pct.toFixed(0)}%`} color={QUANTUM_COLORS.bear} />
                    <Row label="TP2" value={`${s.tp2_pct.toFixed(0)}%`} color={QUANTUM_COLORS.bull} />
                    <Row label="Liquidación" value={`${s.liquidation_rate_pct.toFixed(0)}%`} color={QUANTUM_COLORS.bear} />
                    <Row label="TP3" value={`${s.tp3_pct.toFixed(0)}%`} color={QUANTUM_COLORS.bull} />
                    <Row label="Expectancia" value={`${s.expectancy_pct_margin.toFixed(2)}%`} color={s.expectancy_pct_margin >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear} />
                  </div>
                  <div className="pt-0.5">
                    <Row label="PnL" value={`${s.pnl_usdt >= 0 ? '+' : ''}${s.pnl_usdt.toFixed(2)} USDT`} color={s.pnl_usdt >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear} />
                  </div>
                </Section>
              )}

              {last10.length > 0 && (
                <Section title={`Últimas ${last10.length}`}>
                  <div className="flex flex-col gap-1 max-h-[220px] overflow-y-auto" data-testid="oq-last10">
                    {last10.map((t, i) => {
                      const tps = Array.isArray(t.tps) ? t.tps : []
                      return (
                        <div key={`${t.time}-${i}`} className="flex flex-col" data-testid="oq-last10-row">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-[10px] whitespace-nowrap text-[var(--ink-soft,#4A5A52)]">{formatTime(t.time)}</span>
                            <span className="text-[10px] font-semibold whitespace-nowrap" style={{ color: t.side === 'long' ? _LONG : _SHORT }}>
                              {t.side === 'long' ? 'LONG' : 'SHORT'} <span className="font-mono">{fp(t.entry)}</span>
                            </span>
                            <span className="text-[10px] font-semibold whitespace-nowrap" style={{ color: RESULT_COLOR[t.result] }}>
                              {RESULT_LABEL[t.result] || t.result}
                            </span>
                          </div>
                          <span className="text-[9px] font-mono whitespace-nowrap text-[var(--ink-soft,#4A5A52)]">
                            SL {fp(t.sl)} · TP {tps.map(fp).join(' / ')}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </Section>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
